import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { REPLAY_SPEED } from "@/content/trajectory";
import { DUR, sample, yawOf } from "@/lib/replay";

// ---------------------------------------------------------------------------
// Coordinate mapping (documented per the plan's Task 4 spec)
//
// The recorded trajectory is z-up (x = east, y = north, z = altitude).
// three.js is y-up. Rather than remapping every position/quaternion field
// by hand every frame, the drone's whole rigid-body state (position +
// orientation) is applied UNCHANGED, in trajectory axes, to an inner
// group — and that inner group sits inside an outer group with a single
// fixed rotation of -90° about X (`outer.rotation.set(-PI/2, 0, 0)`).
//
// Rx(-90°) maps a point (x, y, z) -> (x, z, -y), which is exactly the
// z-up -> y-up position remap the plan calls for. Because the SAME fixed
// conjugation is applied to the inner group's entire subtree (both its
// position and its quaternion), a "level" trajectory orientation (i.e.
// one whose body axes are aligned with the world axes, no roll/pitch)
// still renders level in three's y-up world: the outer rotation carries
// the whole rigid frame across coordinate systems together, so relative
// orientation (body-level vs. world-level) is preserved exactly, not
// approximated per-axis. This was verified visually — the takeoff frame
// (t≈0, level hover) renders with the drone's arms horizontal and the
// body flat, not tilted.
// ---------------------------------------------------------------------------

// QuadConfig geometry (matches TwinReplay + the recorded airframe):
// X-frame, rotor mounts at (±0.0778, ±0.0778, 0) — arm length 0.11 m
// (0.0778 * sqrt(2) ≈ 0.11), i.e. a 220 mm motor-to-motor diagonal quad.
const D = 0.0778;
const ROT: [number, number, number][] = [
  [D, D, 0],
  [-D, -D, 0],
  [D, -D, 0],
  [-D, D, 0],
];
// Rotors 0,1 spin CCW, rotors 2,3 spin CW (opposite pairs cancel torque).
const SPIN = [1, 1, -1, -1];
const PROP_R = 0.062;
const MOTOR_R = 0.014;
const MOTOR_H = 0.02;
const PROP_SPIN_RAD_S = 35;
const PAUSE_S = 3;

function readHsl(cs: CSSStyleDeclaration, name: string, fallback: string): string {
  const v = cs.getPropertyValue(name).trim();
  return v ? `hsl(${v})` : fallback;
}

export function DroneHero3D() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tv, setTv] = useState({ t: 0, alt: 0, vz: 0, yaw: 0 });

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const renderer = new THREE.WebGLRenderer({ canvas: cv, antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = true;
    // Soft-shadow variant was deprecated in three 0.184 (silently falls back
    // to PCFShadowMap with a console warning) — use it directly instead.
    renderer.shadowMap.type = THREE.PCFShadowMap;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.05, 100);

    // ---- palette (light lab tokens, read from CSS vars; HSL triples) ----
    const pal = { surface: "hsl(220 33% 98%)", border: "hsl(220 18% 90%)", accent: "hsl(42 95% 52%)" };
    const readPal = () => {
      const cs = getComputedStyle(document.documentElement);
      pal.surface = readHsl(cs, "--surface", pal.surface);
      pal.border = readHsl(cs, "--border", pal.border);
      pal.accent = readHsl(cs, "--accent", pal.accent);
    };
    readPal();
    scene.background = new THREE.Color().setStyle(pal.surface);

    // ---- lights ----
    const hemi = new THREE.HemisphereLight(0xffffff, 0xe4d6bb, 1.15);
    scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, 2.1);
    key.position.set(1.6, 2.6, 1.2);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.near = 0.1;
    key.shadow.camera.far = 8;
    key.shadow.camera.left = -2.2;
    key.shadow.camera.right = 2.2;
    key.shadow.camera.top = 2.2;
    key.shadow.camera.bottom = -2.2;
    key.shadow.bias = -0.0015;
    scene.add(key);

    // ---- ground + grid ----
    const groundGeo = new THREE.PlaneGeometry(20, 20);
    const groundMat = new THREE.ShadowMaterial({ opacity: 0.22 });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    const grid = new THREE.GridHelper(6, 24, new THREE.Color().setStyle(pal.border), new THREE.Color().setStyle(pal.border));
    const gridMats = Array.isArray(grid.material) ? grid.material : [grid.material];
    for (const m of gridMats) {
      m.transparent = true;
      m.opacity = 0.7;
    }
    grid.position.y = 0.001;
    scene.add(grid);

    // ---- drone (parametric, QuadConfig-true) ----
    const outer = new THREE.Group();
    outer.rotation.set(-Math.PI / 2, 0, 0); // z-up (trajectory) -> y-up (three), see block comment above
    scene.add(outer);
    const inner = new THREE.Group();
    // Visual-only enlargement so the true-to-scale (0.11 m arm) airframe
    // reads clearly at the ported camera distances. Group scale affects
    // children geometry only — inner.position (and thus the telemetry)
    // stays in true meters.
    inner.scale.setScalar(1.8);
    outer.add(inner);

    const bodyMat = new THREE.MeshStandardMaterial({ color: 0x22262e, roughness: 0.45, metalness: 0.2 });
    // Slightly lighter/rougher than the body so the arm tubes read as a
    // separate part rather than melting into the center plates.
    const armMat = new THREE.MeshStandardMaterial({ color: 0x2a2f38, roughness: 0.5, metalness: 0.2 });
    const motorMat = new THREE.MeshStandardMaterial({ color: 0x8a8f98, roughness: 0.25, metalness: 0.8 });
    const discMat = new THREE.MeshStandardMaterial({
      color: 0x141821,
      roughness: 0.5,
      metalness: 0.1,
      transparent: true,
      opacity: 0.35,
      side: THREE.DoubleSide,
    });
    const bladeMat = new THREE.MeshStandardMaterial({ color: 0x1a1e26, roughness: 0.4, metalness: 0.3, side: THREE.DoubleSide });
    const noseMat = new THREE.MeshStandardMaterial({ color: new THREE.Color().setStyle(pal.accent), roughness: 0.3, metalness: 0.4 });

    const disposables: THREE.BufferGeometry[] = [];
    const track = <T extends THREE.BufferGeometry>(g: T): T => {
      disposables.push(g);
      return g;
    };

    // Body: two stacked boxes (bottom plate + inset top canopy) for a
    // cheap bevelled-body illusion without extra geometry authoring.
    const lowerGeo = track(new THREE.BoxGeometry(0.08, 0.08, 0.012));
    const lower = new THREE.Mesh(lowerGeo, bodyMat);
    lower.position.z = 0.006;
    lower.castShadow = true;
    inner.add(lower);
    const upperGeo = track(new THREE.BoxGeometry(0.052, 0.052, 0.012));
    const upper = new THREE.Mesh(upperGeo, bodyMat);
    upper.position.z = 0.018;
    upper.castShadow = true;
    inner.add(upper);

    // Nose marker — small gold cap toward the +x arm pair (rotors 0 & 2).
    const noseGeo = track(new THREE.SphereGeometry(0.01, 12, 10));
    const nose = new THREE.Mesh(noseGeo, noseMat);
    nose.position.set(0.05, 0, 0.02);
    nose.castShadow = true;
    inner.add(nose);

    const spinGroups: { group: THREE.Group; spin: number }[] = [];
    const armGeo = track(new THREE.CylinderGeometry(0.006, 0.006, 0.11, 8));
    const motorGeo = track(new THREE.CylinderGeometry(MOTOR_R, MOTOR_R, MOTOR_H, 12));
    const discGeo = track(new THREE.CircleGeometry(PROP_R, 24));
    const bladeGeo = track(new THREE.BoxGeometry(PROP_R * 2, 0.013, 0.003));

    ROT.forEach((mount, k) => {
      const [mx, my] = mount;
      const dir = new THREE.Vector3(mx, my, 0).normalize();

      const arm = new THREE.Mesh(armGeo, armMat);
      arm.position.set(mx / 2, my / 2, 0);
      arm.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
      arm.castShadow = true;
      inner.add(arm);

      const motor = new THREE.Mesh(motorGeo, motorMat);
      motor.position.set(mx, my, MOTOR_H / 2);
      motor.rotation.x = Math.PI / 2; // cylinder's default Y-axis -> local Z (vertical)
      motor.castShadow = true;
      inner.add(motor);

      const spin = new THREE.Group();
      spin.position.set(mx, my, MOTOR_H + 0.008);
      inner.add(spin);

      const disc = new THREE.Mesh(discGeo, discMat);
      spin.add(disc);
      const blade = new THREE.Mesh(bladeGeo, bladeMat);
      blade.castShadow = true;
      spin.add(blade);

      spinGroups.push({ group: spin, spin: SPIN[k]! });
    });

    // ---- camera state: slow auto-orbit + altitude-aware pull-back ----
    let az = 0.9,
      el = 0.42,
      dist = 3.4,
      distS = 3.4,
      targetZ = 0.7,
      autoOrbit = true;

    let W = 0,
      H = 0;
    const resize = () => {
      W = cv.clientWidth;
      H = cv.clientHeight;
      if (!W || !H) return;
      renderer.setSize(W, H, false);
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
    };
    resize();
    // The RO fires once right after observe(), and resizing the drawing
    // buffer can clear the canvas. In animated mode the next rAF repaints;
    // in reduced-motion mode the observer re-renders the static frame.
    let staticRedraw: (() => void) | null = null;
    let lastTvMs = 0;
    const ro = new ResizeObserver(() => {
      resize();
      staticRedraw?.();
    });
    ro.observe(cv);

    // Reused across frames — no per-frame allocations in the hot path.
    const _target = new THREE.Vector3();
    const _offset = new THREE.Vector3();
    const applyCamera = (posZ: number, posX: number, posY: number, dtSec: number) => {
      // Time-based exponential smoothing (tau ~0.33 s): identical camera
      // behavior at any frame rate — a dropped frame no longer doubles the
      // easing step (that lurch read as "jerky" motion).
      const k = 1 - Math.exp(-dtSec * 3.0);
      targetZ += (Math.max(0.45, posZ * 0.85 - 0.15) - targetZ) * k;
      distS += (2.6 + posZ * 0.55 - distS) * k;
      dist = distS;
      // Camera look-at target, converted trajectory (z-up) -> three (y-up)
      // via the same Rx(-90°) mapping used for the drone: (x, z, -y).
      _target.set(posX * 0.8, targetZ, -(posY * 0.8));
      const ce = Math.cos(el);
      _offset.set(
        dist * ce * Math.sin(az),
        dist * Math.sin(el),
        dist * ce * Math.cos(az),
      );
      camera.position.copy(_target).add(_offset);
      camera.lookAt(_target);
    };

    let palTick = 0;
    const renderFrame = (t: number, dtSec: number) => {
      if (!W || !H) resize();
      if (!W || !H) return;
      if ((palTick++ & 63) === 0) {
        readPal();
        (scene.background as THREE.Color).setStyle(pal.surface);
      }
      const s = sample(t);
      const s2 = sample(Math.max(0, t - 0.12));
      const vz = (s.pos[2] - s2.pos[2]) / 0.12;

      inner.position.set(s.pos[0], s.pos[1], s.pos[2]);
      inner.quaternion.set(s.quat[1], s.quat[2], s.quat[3], s.quat[0]); // [w,x,y,z] -> THREE (x,y,z,w)

      for (const { group, spin } of spinGroups) group.rotation.z += dtSec * PROP_SPIN_RAD_S * spin;

      applyCamera(s.pos[2], s.pos[0], s.pos[1], dtSec);
      renderer.render(scene, camera);
      // Telemetry at ~6 Hz: a setState per rAF re-rendered React 60x/s and
      // contended with the render loop — the main perceptible jank source.
      const nowMs = performance.now();
      if (nowMs - lastTvMs > 150) {
        lastTvMs = nowMs;
        setTv({ t, alt: s.pos[2], vz, yaw: Math.round((yawOf(s.quat) * 180) / Math.PI) });
      }
    };

    // Transient drag listeners are tracked so cleanup() can remove them
    // even when the component unmounts mid-drag.
    let dragMove: ((ev: PointerEvent) => void) | null = null;
    let dragUp: (() => void) | null = null;
    const endDrag = () => {
      if (dragMove) cv.removeEventListener("pointermove", dragMove);
      if (dragUp) cv.removeEventListener("pointerup", dragUp);
      dragMove = null;
      dragUp = null;
    };
    const onPointerDown = (e: PointerEvent) => {
      autoOrbit = false;
      cv.setPointerCapture(e.pointerId);
      endDrag();
      let px = e.clientX,
        py = e.clientY;
      dragMove = (ev: PointerEvent) => {
        az -= (ev.clientX - px) * 0.008;
        el = Math.min(1.2, Math.max(0.08, el + (ev.clientY - py) * 0.006));
        px = ev.clientX;
        py = ev.clientY;
        if (reduced) renderFrame(DUR * 0.72, 0);
      };
      dragUp = endDrag;
      cv.addEventListener("pointermove", dragMove);
      cv.addEventListener("pointerup", dragUp);
    };
    cv.addEventListener("pointerdown", onPointerDown);

    let raf = 0;
    const cleanup = () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      cv.removeEventListener("pointerdown", onPointerDown);
      endDrag();
      for (const geometry of disposables) geometry.dispose();
      groundGeo.dispose();
      groundMat.dispose();
      grid.geometry.dispose();
      for (const m of gridMats) m.dispose();
      bodyMat.dispose();
      armMat.dispose();
      motorMat.dispose();
      discMat.dispose();
      bladeMat.dispose();
      noseMat.dispose();
      renderer.dispose();
    };

    if (reduced) {
      // Single static frame: the camera-follow low-pass (distS/targetZ)
      // normally converges over many frames — seed it at the converged
      // values for the target sample so the drone is actually in frame.
      const s0 = sample(DUR * 0.72);
      distS = 2.6 + s0.pos[2] * 0.55;
      dist = distS;
      targetZ = Math.max(0.45, s0.pos[2] * 0.85 - 0.15);
      staticRedraw = () => renderFrame(DUR * 0.72, 0);
      staticRedraw();
      return cleanup;
    }

    let start: number | null = null;
    let lastNow = 0;
    const loop = (now: number) => {
      if (start === null) start = now;
      const dtSec = lastNow ? (now - lastNow) / 1000 : 1 / 60;
      lastNow = now;
      if (autoOrbit) az += dtSec * 0.075;
      const t = Math.min((((now - start) / 1000) * REPLAY_SPEED) % (DUR + PAUSE_S), DUR);
      renderFrame(t, dtSec);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return cleanup;
  }, []);

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="block h-[420px] w-full cursor-grab touch-pan-y active:cursor-grabbing sm:h-[540px]"
        aria-label="3D digital-twin viewport replaying a quadcopter takeoff computed by the Workshop's functional models and NVIDIA Isaac Sim physics."
      />
      <div className="grid grid-cols-2 border-t border-border bg-card font-mono sm:grid-cols-4">
        {(
          [
            ["t", `${tv.t.toFixed(2)} s`],
            ["altitude", `${tv.alt.toFixed(2)} m`],
            ["v-speed", `${tv.vz >= 0 ? "+" : ""}${tv.vz.toFixed(2)} m/s`],
            ["yaw", `${tv.yaw}°`],
          ] as const
        ).map(([k, v], i) => (
          <div key={k} className={`px-4 py-3 ${i ? "border-l border-border" : ""}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{k}</div>
            <div className="mt-0.5 text-lg text-primary tabular-nums">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
